import { useState } from 'react';
import { humanize, Icon, StateBox, WeakestLinkBanner } from '@garnet/ui';
import { canSubmit, captureProgress } from '../data/shape.js';
import type { InspectorProfile, InspectorTask } from '../data/types.js';
import { CardLabel, PhoneButton } from '../components/PhoneChrome.js';

/**
 * Submit — review, sign, sync (PRD §14.3). Submission is BLOCKED until every required
 * capture for the method is present (PRD §10.6); the gate is `shape.canSubmit`, re-checked
 * here so the signed act cannot escape the template. Offline signs into the buffer and
 * syncs on reconnection (PRD §10.4).
 */
export function Submit({
  task,
  profile,
  captured,
  values,
  offline,
  submitted,
  onSubmit,
  onBackToCapture,
}: {
  task: InspectorTask | null;
  profile: InspectorProfile;
  captured: ReadonlySet<string>;
  values: Record<string, string>;
  offline: boolean;
  submitted: boolean;
  onSubmit: () => void;
  onBackToCapture: () => void;
}) {
  const [signed, setSigned] = useState(false);

  if (!task) {
    return (
      <div style={{ padding: 14 }}>
        <StateBox kind="empty" title="No task to submit">Capture a task first.</StateBox>
      </div>
    );
  }

  const progress = captureProgress(task.method, captured);
  const gateOpen = canSubmit(task.method, captured);
  const valued = Object.entries(values).filter(([, v]) => v !== '');

  return (
    <div style={{ padding: 14, display: 'grid', gap: 14 }}>
      <div style={{ background: 'var(--panel)', border: '1px solid var(--rule)', borderRadius: 10, padding: 14 }}>
        <CardLabel>{task.id} · {task.method_label}</CardLabel>
        <div style={{ fontWeight: 600, marginTop: 5 }}>{task.asset}</div>
        <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px', margin: '12px 0 0', fontFamily: 'var(--mono)', fontSize: 12 }}>
          <dt style={{ color: 'var(--muted)' }}>carrier</dt>
          <dd style={{ margin: 0, wordBreak: 'break-all' }}>{task.carrier}</dd>
          {valued.map(([k, v]) => (
            <div key={k} style={{ display: 'contents' }}>
              <dt style={{ color: 'var(--muted)' }}>{humanize(k)}</dt>
              <dd style={{ margin: 0 }}>{v}</dd>
            </div>
          ))}
          <dt style={{ color: 'var(--muted)' }}>captures</dt>
          <dd style={{ margin: 0 }}>{progress.done}/{progress.total}</dd>
        </dl>
      </div>

      {!gateOpen ? (
        <>
          <WeakestLinkBanner tone="flag">
            Submission blocked — {progress.missing.length} required capture{progress.missing.length === 1 ? '' : 's'} still
            missing ({progress.missing.map(humanize).join(', ')}). PRD §10.6.
          </WeakestLinkBanner>
          <PhoneButton primary full onClick={onBackToCapture}>
            Back to capture
          </PhoneButton>
        </>
      ) : (
        <>
          <WeakestLinkBanner tone="attest">
            This signed act resolves <strong>{task.resolves_check_label}</strong> to verified · attestation (gold).
          </WeakestLinkBanner>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'var(--mono)', fontSize: 12.5 }}>
            <input type="checkbox" checked={signed} onChange={(e) => setSigned(e.target.checked)} style={{ width: 17, height: 17 }} />
            I attest this evidence is true · {profile.name}
          </label>
          {submitted ? (
            <div
              className="weak"
              style={{ borderLeftColor: 'var(--verified)', background: 'var(--verified-bg)', borderRadius: 8 }}
              role="status"
            >
              <span className="wl" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--verified-ink)' }}>
                <Icon name={offline ? 'clock' : 'check'} size={13} />
                {offline ? 'queued' : 'synced'}
              </span>
              <span className="wt">
                {offline
                  ? 'Held in the offline buffer — syncs when a connection returns (PRD §10.4).'
                  : 'Attestation signed and synced. The job resumes at the engine.'}
              </span>
            </div>
          ) : (
            <PhoneButton primary full disabled={!signed} onClick={onSubmit}>
              {offline ? 'Sign & queue' : 'Sign & sync'}
            </PhoneButton>
          )}
        </>
      )}
    </div>
  );
}
